import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, 
  Check, 
  Zap,
  Building2,
  Sparkles
} from 'lucide-react';

export const Pricing = () => {
  const plans = [
    {
      name: "Starter",
      description: "Perfect for small businesses getting started with transaction tracking.",
      price: "$29",
      period: "/month",
      features: [
        "Up to 500 transactions/month",
        "Real-time SMS parsing",
        "Basic transaction history",
        "Email notifications",
        "Mobile app access",
        "Email support"
      ],
      cta: "Start Free Trial",
      ctaVariant: "outline",
      popular: false
    },
    {
      name: "Professional",
      description: "For growing businesses that need advanced features and analytics.",
      price: "$79",
      period: "/month",
      features: [
        "Up to 5,000 transactions/month",
        "Everything in Starter",
        "Advanced analytics dashboard",
        "Transaction categorization",
        "Export & reporting",
        "API access",
        "Priority support",
        "Team accounts (up to 5)"
      ],
      cta: "Start Free Trial",
      ctaVariant: "default",
      popular: true
    },
    {
      name: "Enterprise",
      description: "Custom solutions for large organizations with high volume needs.",
      price: "Custom",
      period: "",
      features: [
        "Unlimited transactions",
        "Everything in Professional",
        "Dedicated account manager",
        "Custom integrations",
        "SLA guarantee",
        "On-premise deployment option",
        "24/7 phone support",
        "Unlimited team members"
      ],
      cta: "Contact Sales",
      ctaVariant: "outline",
      popular: false
    }
  ];

  const faqs = [
    {
      question: "Can I change plans later?",
      answer: "Yes, you can upgrade or downgrade your plan at any time. Changes take effect at the start of your next billing cycle."
    },
    {
      question: "Is there a free trial?",
      answer: "Yes, all plans come with a 14-day free trial. No credit card required to start."
    },
    {
      question: "What happens if I exceed my transaction limit?",
      answer: "We'll notify you when you're approaching your limit. You can upgrade your plan or purchase additional transactions."
    },
    {
      question: "Do you offer annual billing?",
      answer: "Yes, annual billing is available with a 20% discount on all plans."
    }
  ];

  return (
    <div data-testid="pricing-page">
      {/* Hero Section */}
      <section className="pt-32 pb-16 lg:pt-40 lg:pb-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <span className="text-sm font-medium text-blue-600 uppercase tracking-widest mb-4 block animate-fade-in">
              Pricing
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-tight mb-6 animate-fade-in-up">
              Simple, Transparent Pricing
            </h1>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed animate-fade-in-up delay-100">
              Choose the plan that fits your business. Start with a free trial, upgrade as you grow.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-20 lg:pb-28 bg-white" data-testid="pricing-cards">
        <div className="container-main">
          <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {plans.map((plan, index) => (
              <div 
                key={index}
                className={`pricing-card ${plan.popular ? 'featured ring-2 ring-[#0077B6]' : ''}`}
                data-testid={`pricing-plan-${plan.name.toLowerCase()}`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-[#0077B6] text-white text-sm font-medium px-4 py-1 rounded-full flex items-center gap-1">
                      <Sparkles className="w-4 h-4" />
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    {index === 0 && <Zap className="w-5 h-5 text-[#0077B6]" />}
                    {index === 1 && <Sparkles className="w-5 h-5 text-[#0077B6]" />}
                    {index === 2 && <Building2 className="w-5 h-5 text-[#0077B6]" />}
                    <h3 className="text-xl font-semibold text-slate-900">{plan.name}</h3>
                  </div>
                  <p className="text-slate-600 text-sm">{plan.description}</p>
                </div>

                <div className="mb-6">
                  <span className="text-4xl font-bold text-slate-900">{plan.price}</span>
                  <span className="text-slate-500">{plan.period}</span>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start gap-3 text-sm">
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-600">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link to={plan.name === "Enterprise" ? "/contact" : "/contact?type=demo"} className="block">
                  <Button 
                    className={`w-full ${plan.popular ? 'btn-primary' : 'btn-secondary'}`}
                    data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                  >
                    {plan.cta}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Comparison */}
      <section className="section-padding bg-slate-50" data-testid="features-comparison">
        <div className="container-main">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              All Plans Include
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {[
              "SSL encryption",
              "Mobile app access",
              "Transaction history",
              "Email notifications",
              "Basic analytics",
              "Cloud backup",
              "99.9% uptime",
              "Regular updates"
            ].map((feature, index) => (
              <div key={index} className="flex items-center gap-3 bg-white p-4 rounded-lg">
                <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                <span className="text-slate-700 text-sm">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SIMVault Pricing Note */}
      <section className="section-padding bg-white" data-testid="simvault-pricing">
        <div className="container-main">
          <div className="max-w-3xl mx-auto bg-[#0A1628] rounded-3xl p-10 text-center text-white">
            <h3 className="text-2xl font-semibold mb-4">SIMVault Pricing</h3>
            <p className="text-slate-300 mb-6">
              SIMVault pricing is customized based on your specific needs including number of 
              SIMs, security requirements, and service level agreements.
            </p>
            <Link to="/contact">
              <Button className="btn-accent" data-testid="simvault-pricing-contact">
                Contact Us for a Quote
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-slate-50" data-testid="pricing-faq">
        <div className="container-main">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-semibold text-slate-900 mb-4">
                Frequently Asked Questions
              </h2>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-xl p-6 border border-slate-100"
                  data-testid={`pricing-faq-${index}`}
                >
                  <h4 className="font-semibold text-slate-900 mb-2">{faq.question}</h4>
                  <p className="text-slate-600">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#0A1628]" data-testid="pricing-cta">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-slate-300 text-lg mb-10 leading-relaxed">
              Start your 14-day free trial today. No credit card required.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact?type=demo">
                <Button className="btn-accent text-base px-8 py-6" data-testid="pricing-cta-demo">
                  Start Free Trial
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="bg-transparent text-white border-white/30 hover:bg-white/10 rounded-full px-8 py-6 text-base" data-testid="pricing-cta-contact">
                  Talk to Sales
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
